import { useEffect, useState } from "react";
import { useDiet } from "@/contexts/DietContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Lock, Leaf, ArrowLeftRight, Pill, ShoppingCart, Download, Loader2,
  FileText, Ruler, BookOpen, Heart, Apple, Dumbbell, Coffee, Salad,
  Plus, Pencil, Trash2, Files,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api";
import { toast } from "sonner";
import { MaterialFormDialog, MaterialRecord, MaterialFile } from "@/components/materials/MaterialFormDialog";

const ICONS: Record<string, any> = {
  FileText, Leaf, ArrowLeftRight, Pill, ShoppingCart, Ruler,
  BookOpen, Heart, Apple, Dumbbell, Coffee, Salad,
};

const PLAN_LABEL: Record<string, string> = {
  basic: "Basic", plus: "Plus", complete: "Max",
};

const Materiais = () => {
  const { selectedPlan } = useDiet();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState<MaterialRecord[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loadingFile, setLoadingFile] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<MaterialRecord | null>(null);
  const [deleting, setDeleting] = useState<MaterialRecord | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [filesModal, setFilesModal] = useState<MaterialRecord | null>(null);

  const tiers = ["basic", "plus", "complete"];
  const hasAccess = tiers.indexOf(selectedPlan || "") >= tiers.indexOf("complete");

  const load = async () => {
    setLoading(true);
    try {
      const data = await apiFetch<{ materials: MaterialRecord[]; isAdmin: boolean }>(
        "api-materials", { action: "list" }
      );
      setMaterials(data.materials || []);
      setIsAdmin(!!data.isAdmin);
    } catch (e: any) {
      toast.error(e.message || "Erro ao carregar materiais");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasAccess || selectedPlan) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan]);

  const handleDownloadFile = async (file: MaterialFile) => {
    setLoadingFile(file.file_path);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Sessão expirada. Faça login novamente.");

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const res = await fetch(
        `${supabaseUrl}/functions/v1/api-materials?file=${encodeURIComponent(file.file_path)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error || "Erro ao baixar o material");
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_name || file.file_path.split("/").pop() || "material.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e.message || "Erro ao baixar o material");
    } finally {
      setLoadingFile(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeletingId(deleting.id);
    try {
      await apiFetch("api-materials", { action: "delete", id: deleting.id }, { method: "DELETE" });
      toast.success("Material excluído");
      setDeleting(null);
      load();
    } catch (e: any) {
      toast.error(e.message || "Erro ao excluir");
    } finally {
      setDeletingId(null);
    }
  };

  // Block non-Max users (unless admin viewing)
  if (!hasAccess && !isAdmin && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
          <Lock className="w-7 h-7 text-muted-foreground" />
        </div>
        <h1 className="text-2xl font-bold text-foreground">Materiais Exclusivos</h1>
        <p className="text-muted-foreground max-w-sm">
          PDFs exclusivos disponíveis apenas no plano <strong>PRO FIT MAX</strong>. Faça upgrade para acessar todos os materiais.
        </p>
        <Button onClick={() => navigate("/plans")}>Ver Planos</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Materiais</h1>
          <p className="text-muted-foreground text-sm mt-1">PDFs exclusivos do plano PRO FIT MAX</p>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Novo
          </Button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : materials.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>Nenhum material disponível ainda.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {materials.map((m) => {
            const Icon = ICONS[m.icon] || FileText;
            const inactive = !m.is_active;
            const files = m.files || [];
            const isMulti = files.length > 1;

            return (
              <Card key={m.id} className={`hover:shadow-md transition-shadow flex flex-col h-full ${inactive ? "opacity-60" : ""}`}>
                <CardContent className="p-5 flex flex-col flex-1 gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 relative">
                      <Icon className="w-5 h-5 text-primary" />
                      {isMulti && (
                        <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center">
                          {files.length}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <h2 className="font-semibold text-foreground leading-tight [overflow-wrap:anywhere] min-w-0 flex-1">{m.title}</h2>
                        {isAdmin && (
                          <span className="text-[10px] rounded-full bg-muted text-muted-foreground px-2 py-0.5 shrink-0">
                            {PLAN_LABEL[m.required_plan] || m.required_plan}
                            {inactive && " · Inativo"}
                          </span>
                        )}
                      </div>
                      {m.description && (
                        <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{m.description}</p>
                      )}
                      {isMulti && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-0.5">
                          <Files className="w-3.5 h-3.5" />
                          {files.length} arquivos disponíveis
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Action footer pinned to the bottom */}
                  <div className="mt-auto pt-3 border-t border-border/60 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      {files.length === 0 ? (
                        <span className="text-xs text-muted-foreground italic">Sem arquivos anexados</span>
                      ) : !isMulti ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={loadingFile === files[0].file_path}
                          onClick={() => handleDownloadFile(files[0])}
                        >
                          {loadingFile === files[0].file_path ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Download className="w-3.5 h-3.5" />
                          )}
                          Baixar PDF
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setFilesModal(m)}>
                          <Files className="w-3.5 h-3.5" />
                          Ver arquivos
                        </Button>
                      )}
                    </div>

                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setEditing(m); setFormOpen(true); }}
                        >
                          <Pencil className="w-3.5 h-3.5" /> Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleting(m)}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Excluir
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <MaterialFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        material={editing}
        onSaved={load}
      />

      <Dialog open={!!filesModal} onOpenChange={(v) => !v && setFilesModal(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="[overflow-wrap:anywhere] pr-8">{filesModal?.title}</DialogTitle>
            <DialogDescription>
              {filesModal?.files?.length} arquivos disponíveis
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-1.5 overflow-y-auto overflow-x-hidden -mx-1 px-1">
            {(filesModal?.files || []).map((f) => (
              <li
                key={f.id}
                className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-2 min-w-0"
              >
                <FileText className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm text-foreground truncate flex-1 min-w-0" title={f.original_name}>
                  {f.original_name}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 shrink-0"
                  disabled={loadingFile === f.file_path}
                  onClick={() => handleDownloadFile(f)}
                >
                  {loadingFile === f.file_path ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5" />
                  )}
                </Button>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir material?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleting?.title}" será removido permanentemente, junto com todos os arquivos PDF anexados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingId}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={!!deletingId}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingId && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Materiais;
